import React, { useState } from 'react';
import { request, fmt } from '../api.js';
import { Link } from '../router.jsx';
import { useLoad, Status, Err, Empty } from './user.jsx';

const Stat = ({ label, value, sub }) => <div className="card stat"><div className="label">{label}</div><div className="value">{value}</div>{sub && <div className="sub">{sub}</div>}</div>;

export function AdminOverview() {
  const { data: d, error, reload } = useLoad('/v1/admin/stats');
  return (
    <>
      <div className="page-head"><div><h1>Overview</h1><p className="muted">Forge Cloud at a glance.</p></div><button className="btn" onClick={reload}>Refresh</button></div>
      <Err error={error} />
      {d && <>
        <div className="grid">
          <Stat label="Users" value={d.users.total} sub={`${d.users.new_7d} new this week · ${d.users.active_1d} active today`} />
          <Stat label="Tokens this month" value={fmt.tokens(d.usage.month_tokens)} sub={`${fmt.tokens(d.usage.today_tokens)} today`} />
          <Stat label="Model spend (list price)" value={fmt.usd(d.usage.month_cost_usd)} sub={`${d.usage.month_requests} requests · ${d.usage.month_errors} errors`} />
          <Stat label="Cloud sessions" value={d.sessions.active} sub={`active · ${d.sessions.last_24h} in 24h · ${d.sessions.failed_24h} failed`} />
          <Stat label="Schedules" value={d.schedules.enabled} sub={`enabled of ${d.schedules.total}`} />
          <Stat label="Shares" value={d.shares.total} sub={`${(d.shares.bytes / 1e6).toFixed(1)} MB in R2`} />
        </div>
        <h2>Health</h2>
        <div className="table-wrap"><table><tbody>
          <tr><td>Neon Postgres</td><td><span className="pill ok">ok</span></td></tr>
          <tr><td>Redis</td><td><span className={`pill ${d.health.redis.ok ? 'ok' : 'bad'}`}>{d.health.redis.ok ? 'ok' : d.health.redis.error}</span></td></tr>
          {d.queues && Object.entries(d.queues).map(([q, c]) => <tr key={q}><td>BullMQ · {q}</td><td className="mono">{c.active} active · {c.waiting} waiting · {c.delayed} delayed · {c.completed} done · {c.failed} failed</td></tr>)}
        </tbody></table></div>
      </>}
    </>
  );
}

export function AdminUsers() {
  const [q, setQ] = useState('');
  const list = useLoad(`/v1/admin/users?q=${encodeURIComponent(q)}`, [q]);
  const plans = useLoad('/v1/admin/plans');
  const [error, setError] = useState(null);
  const patch = async (id, body) => { setError(null); try { await request(`/v1/admin/users/${id}`, { method: 'PATCH', json: body }); list.reload(); } catch (e) { setError(e.message); } };
  return (
    <>
      <div className="page-head"><div><h1>Users</h1><p className="muted">Accounts come from Neon Auth; plan, role and credits live in Forge Cloud.</p></div>
        <input style={{ maxWidth: 280 }} placeholder="Search email or name" value={q} onChange={e => setQ(e.target.value)} aria-label="Search users" /></div>
      <Err error={error || list.error} />
      <div className="table-wrap"><table><thead><tr><th>Email</th><th>Plan</th><th>Role</th><th>Tokens (month)</th><th>Spend</th><th>Credits</th><th>Last seen</th><th /></tr></thead>
        <tbody>{(list.data?.users || []).map(u => (
          <tr key={u.id}>
            <td>{u.email}{u.disabled && <> <span className="pill bad">disabled</span></>}</td>
            <td><select value={u.plan_id} onChange={e => patch(u.id, { plan_id: e.target.value })} aria-label="Plan">{(plans.data?.plans || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></td>
            <td><select value={u.role} onChange={e => patch(u.id, { role: e.target.value })} aria-label="Role"><option>user</option><option>admin</option></select></td>
            <td>{fmt.tokens(u.month_tokens)}</td><td>{fmt.usd(u.month_cost_usd)}</td><td>{fmt.usd(u.credit_cents / 100)}</td><td>{fmt.ago(u.last_seen_at)}</td>
            <td className="row">
              <button className="btn" onClick={() => { const v = prompt('Credits to add in dollars (negative to remove):', '10'); if (v) patch(u.id, { add_credit_cents: Math.round(Number(v) * 100) }); }}>Credits</button>
              <button className={`btn ${u.disabled ? '' : 'danger'}`} onClick={() => patch(u.id, { disabled: !u.disabled })}>{u.disabled ? 'Enable' : 'Disable'}</button>
            </td>
          </tr>
        ))}</tbody></table></div>
    </>
  );
}

export function AdminUsage() {
  const [days, setDays] = useState(30);
  const { data: d, error } = useLoad(`/v1/admin/usage?days=${days}`, [days]);
  const max = Math.max(1, ...(d?.daily || []).map(x => x.input + x.output));
  return (
    <>
      <div className="page-head"><div><h1>Usage &amp; models</h1><p className="muted">Everything that went through the AI proxy.</p></div>
        <select style={{ maxWidth: 160 }} value={days} onChange={e => setDays(Number(e.target.value))} aria-label="Period">{[7, 30, 90, 180].map(n => <option key={n} value={n}>Last {n} days</option>)}</select></div>
      <Err error={error} />
      {d && <>
        <div className="card"><div className="bars">{d.daily.map(x => <i key={x.day} style={{ height: `${((x.input + x.output) / max) * 100}%` }} title={`${x.day.slice(0, 10)} · ${fmt.tokens(x.input + x.output)} tokens · ${fmt.usd(x.cost_usd)}`} />)}</div></div>
        <h2>Models</h2>
        <div className="table-wrap"><table><thead><tr><th>Provider</th><th>Model</th><th>Requests</th><th>Tokens</th><th>Cost</th><th>Avg latency</th><th>Errors</th></tr></thead>
          <tbody>{d.models.map(m => <tr key={m.provider + m.model}><td>{m.provider}</td><td className="mono">{m.model}{!m.priced && <> <span className="pill warn" title="No price in pricing.js">unpriced</span></>}</td><td>{m.requests}</td><td>{fmt.tokens(m.tokens)}</td><td>{fmt.usd(m.cost_usd)}</td><td>{(m.avg_ms / 1000).toFixed(1)}s</td><td>{m.errors}</td></tr>)}</tbody></table></div>
        <h2>Top users</h2>
        <div className="table-wrap"><table><thead><tr><th>User</th><th>Tokens</th><th>Cost</th></tr></thead>
          <tbody>{d.top_users.map(u => <tr key={u.id}><td>{u.email}</td><td>{fmt.tokens(u.tokens)}</td><td>{fmt.usd(u.cost_usd)}</td></tr>)}</tbody></table></div>
      </>}
    </>
  );
}

export function AdminPlans() {
  const list = useLoad('/v1/admin/plans');
  const [error, setError] = useState(null);
  const save = async (p, field, value) => { setError(null); try { await request(`/v1/admin/plans/${p.id}`, { method: 'PATCH', json: { [field]: Number(value) } }); list.reload(); } catch (e) { setError(e.message); } };
  const Cell = ({ p, f }) => <td><input type="number" defaultValue={p[f]} onBlur={e => Number(e.target.value) !== Number(p[f]) && save(p, f, e.target.value)} style={{ width: 140 }} aria-label={f} /></td>;
  return (
    <>
      <div className="page-head"><div><h1>Plans &amp; quotas</h1><p className="muted">Changes apply on the next request. Edit a value, then leave the field to save.</p></div></div>
      <Err error={error || list.error} />
      <div className="table-wrap"><table><thead><tr><th>Plan</th><th>Tokens / month</th><th>Requests / min</th><th>Cloud sessions</th><th>Schedules</th><th>Price (cents)</th></tr></thead>
        <tbody>{(list.data?.plans || []).map(p => <tr key={p.id}><td><b>{p.name}</b> <span className="muted mono">{p.id}</span></td><Cell p={p} f="monthly_tokens" /><Cell p={p} f="requests_per_min" /><Cell p={p} f="max_sessions" /><Cell p={p} f="max_schedules" /><Cell p={p} f="price_cents" /></tr>)}</tbody></table></div>
    </>
  );
}

export function AdminSessions() {
  const list = useLoad('/v1/admin/sessions');
  const cancel = async id => { await request(`/v1/admin/sessions/${id}/cancel`, { method: 'POST' }); list.reload(); };
  return (
    <>
      <div className="page-head"><div><h1>All cloud sessions</h1><p className="muted">E2B sandboxes started by every user.</p></div><button className="btn" onClick={list.reload}>Refresh</button></div>
      <Err error={list.error} />
      {list.data?.sessions?.length ? <div className="table-wrap"><table><thead><tr><th>User</th><th>Task</th><th>Status</th><th>Sandbox</th><th>Started</th><th>Finished</th><th /></tr></thead>
        <tbody>{list.data.sessions.map(s => <tr key={s.id}><td>{s.email}</td><td className="wrap">{s.title}</td><td><Status s={s.status} /></td><td className="mono">{s.sandbox_id || '—'}</td><td>{fmt.ago(s.created_at)}</td><td>{fmt.ago(s.finished_at)}</td>
          <td>{['queued', 'starting', 'running'].includes(s.status) && <button className="btn danger" onClick={() => cancel(s.id)}>Cancel</button>}</td></tr>)}</tbody></table></div> : !list.loading && <Empty>No cloud sessions yet.</Empty>}
    </>
  );
}

export function AdminShares() {
  const list = useLoad('/v1/admin/shares');
  const del = async id => { if (confirm('Delete this share for its owner too?')) { await request(`/v1/admin/shares/${id}`, { method: 'DELETE' }); list.reload(); } };
  return (
    <>
      <div className="page-head"><div><h1>All shares</h1><p className="muted">Public conversation links. Remove anything that shouldn't be public.</p></div></div>
      <Err error={list.error} />
      {list.data?.shares?.length ? <div className="table-wrap"><table><thead><tr><th>Owner</th><th>Title</th><th>Link</th><th>Views</th><th>Created</th><th /></tr></thead>
        <tbody>{list.data.shares.map(s => <tr key={s.id}><td>{s.email}</td><td className="wrap">{s.title}</td><td><Link to={`/s/${s.id}`}>/s/{s.id}</Link></td><td>{s.views}</td><td>{fmt.ago(s.created_at)}</td><td><button className="btn danger" onClick={() => del(s.id)}>Delete</button></td></tr>)}</tbody></table></div> : !list.loading && <Empty>No shares.</Empty>}
    </>
  );
}

export function AdminAudit() {
  const list = useLoad('/v1/admin/audit');
  return (
    <>
      <div className="page-head"><div><h1>Audit log</h1><p className="muted">Token issues and revocations, admin changes, deletions.</p></div></div>
      <Err error={list.error} />
      <div className="table-wrap"><table><thead><tr><th>When</th><th>Who</th><th>Action</th><th>Target</th><th>Detail</th></tr></thead>
        <tbody>{(list.data?.events || []).map(e => <tr key={e.id}><td>{fmt.date(e.created_at)}</td><td>{e.actor_email || e.actor_id || 'system'}</td><td className="mono">{e.action}</td><td className="mono">{e.target}</td><td className="mono wrap">{JSON.stringify(e.detail)}</td></tr>)}</tbody></table></div>
    </>
  );
}
